import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { ShieldCheck, Save, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';
import GroupedPermissions from '../../components/GroupedPermissions';
import { apiErrorMessage } from '../../lib/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_OPTIONS = ['super_admin', 'service_manager', 'office_staff', 'construction_manager', 'technician', 'admin', 'manager', 'employee'];
const DEPT_OPTIONS = ['service', 'new_construction', 'office'];
// iter90 — Show every internal staff role on the Roles & Permissions list. Previously the
// filter only included ['admin','manager','employee'] which hid super_admin, service_manager,
// office_staff, construction_manager, and technician users from the page entirely.
const INTERNAL_STAFF_ROLES = new Set([
  'super_admin', 'admin', 'manager', 'service_manager', 'construction_manager',
  'office_staff', 'technician', 'employee',
]);

const AdminPermissions = () => {
  const [catalog, setCatalog] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [perms, setPerms] = useState({ permissions: [], role: '', name: '' });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/permissions/catalog`, { withCredentials: true }),
      axios.get(`${API}/admin/employees`, { withCredentials: true }),
    ]).then(([c, e]) => {
      setCatalog(c.data);
      setUsers(e.data);
    });
  }, []);

  const loadUser = async (id) => {
    setSelectedId(id);
    const { data } = await axios.get(`${API}/admin/permissions/${id}`, { withCredentials: true });
    const u = users.find(x => x.id === id);
    setPerms({ ...data, department: u?.department || '' });
  };

  const togglePerm = (p) => {
    setPerms({ ...perms, permissions: perms.permissions.includes(p) ? perms.permissions.filter(x => x !== p) : [...perms.permissions, p] });
  };

  const save = async () => {
    try {
      await axios.put(`${API}/admin/permissions`, { user_id: selectedId, permissions: perms.permissions }, { withCredentials: true });
      toast.success('Permissions saved');
    } catch (err) { toast.error(apiErrorMessage(err, 'Failed')); }
  };

  const changeRole = async (role) => {
    // iter164 — Field-test fix: changing role MUST keep all 4 records in sync
    // (users.role, role_permissions override, /auth/me payload, and the UI row).
    // The admin explicitly picks whether to:
    //   - Apply new role defaults (recommended — clears any stale override)
    //   - Keep current custom permissions
    //   - Cancel
    if (role === perms.role) return; // no-op
    const currentRoleDefaults = (catalog.default_role_permissions || {})[perms.role] || [];
    const isCustom = perms.is_override || (
      perms.permissions.slice().sort().join('|') !== currentRoleDefaults.slice().sort().join('|')
    );
    let applyDefaults = true;
    if (isCustom) {
      const choice = window.confirm(
        `Change role to "${role.replace(/_/g, ' ')}"?\n\n` +
        `This user currently has custom permissions.\n\n` +
        `• Click OK to APPLY ROLE DEFAULTS (recommended — clears the custom overrides).\n` +
        `• Click Cancel to KEEP CUSTOM PERMISSIONS (role changes but permissions stay).\n\n` +
        `To abort entirely, refresh the page.`,
      );
      applyDefaults = choice;
    } else {
      // No custom override — proceed silently; defaults already apply.
      const ok = window.confirm(`Change role to "${role.replace(/_/g, ' ')}"?`);
      if (!ok) return;
    }
    try {
      await axios.put(
        `${API}/admin/users/role`,
        { user_id: selectedId, role, apply_defaults: applyDefaults },
        { withCredentials: true },
      );
      toast.success(applyDefaults ? `Role changed and ${role} defaults applied` : 'Role changed (custom permissions kept)');
      // Refresh both the staff list and the selected user
      const e = await axios.get(`${API}/admin/employees`, { withCredentials: true });
      setUsers(e.data || []);
      loadUser(selectedId);
    } catch (err) { toast.error(apiErrorMessage(err, 'Failed')); }
  };

  // iter90 — Department change handler (previously referenced from JSX but not declared,
  // which crashed the page the moment any dept button was clicked).
  const changeDept = async (department) => {
    try {
      await axios.put(`${API}/admin/users/department`, { user_id: selectedId, department }, { withCredentials: true });
      toast.success('Department changed');
      const e = await axios.get(`${API}/admin/employees`, { withCredentials: true });
      setUsers(e.data || []);
      setPerms((prev) => ({ ...prev, department }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change department');
    }
  };

  // iter90 — Quick filter input for the staff list
  const [filterQ, setFilterQ] = useState('');

  // iter163 — Phase 2: Default to the new grouped view. The legacy flat grid is
  // kept behind a toggle so we have a one-click rollback if the grouped view
  // causes confusion during field-testing. Per-user preference persists in
  // localStorage so a flip stays sticky across sessions.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const stored = localStorage.getItem('nexflow_perms_view_mode');
      return stored === 'flat' ? 'flat' : 'grouped';
    } catch {
      return 'grouped';
    }
  });
  const setViewModePersisted = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('nexflow_perms_view_mode', mode); } catch { /* ignore */ }
  };

  const resetToDefault = () => {
    if (!catalog || !perms.role) return;
    setPerms({ ...perms, permissions: catalog.default_role_permissions[perms.role] || [] });
  };

  if (!catalog) return <div className="text-[#A3A3A3]">Loading...</div>;

  return (
    <div data-testid="admin-permissions-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[#D4AF37]" /> Roles & Permissions</h2>
        <p className="text-[#A3A3A3]">Promote, demote, or fine-tune what each employee can access</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-[#141414] border border-[#262626] rounded-md p-4 max-h-[600px] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Staff</p>
            <span className="text-[10px] text-[#525252]" data-testid="staff-count">{users.filter(u => INTERNAL_STAFF_ROLES.has(u.role)).length} users</span>
          </div>
          <input
            data-testid="staff-filter"
            value={filterQ}
            onChange={(e) => setFilterQ(e.target.value)}
            placeholder="Filter by name, email, role…"
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-xs text-white placeholder-[#525252] mb-3 focus:outline-none focus:border-[#D4AF37]"
          />
          <div className="space-y-1">
            {users
              .filter((u) => INTERNAL_STAFF_ROLES.has(u.role))
              .filter((u) => {
                if (!filterQ) return true;
                const q = filterQ.toLowerCase();
                return (u.name || '').toLowerCase().includes(q)
                    || (u.email || '').toLowerCase().includes(q)
                    || (u.role || '').toLowerCase().includes(q)
                    || (u.department || '').toLowerCase().includes(q);
              })
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map((u) => (
              <button
                key={u.id}
                data-testid={`user-row-${u.id}`}
                onClick={() => loadUser(u.id)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selectedId === u.id ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'hover:bg-[#262626] text-white'}`}
              >
                <p className="font-bold">{u.name || u.email}</p>
                <p className={`text-xs ${selectedId === u.id ? 'text-[#0A0A0A]/70' : 'text-[#A3A3A3]'}`}>
                  <span className="font-mono">{u.role}</span>
                  {u.department ? ` · ${u.department}` : ''}
                  {' · '}{u.email}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4">
          {!selectedId ? (
            <p className="text-[#A3A3A3] text-center py-12">Select a staff member to manage their access</p>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-white text-lg font-bold">{perms.name}</p>
                  <p className="text-[#A3A3A3] text-sm">Role: <span className="text-[#D4AF37]">{perms.role}</span> • Dept: <span className="text-[#D4AF37]">{perms.department || '—'}</span></p>
                </div>
              </div>

              <div className="border-t border-[#262626] pt-3">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Role</p>
                <div className="flex gap-2 flex-wrap">
                  {ROLE_OPTIONS.map(r => (
                    <Button
                      key={r}
                      data-testid={`role-${r}`}
                      onClick={() => changeRole(r)}
                      variant={perms.role === r ? 'default' : 'outline'}
                      size="sm"
                      className={perms.role === r ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]' : 'border-[#262626] text-white hover:bg-[#262626]'}
                    >
                      {r.replace(/_/g, ' ')}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border-t border-[#262626] pt-3">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Department</p>
                <div className="flex gap-2 flex-wrap">
                  {DEPT_OPTIONS.map(d => (
                    <Button
                      key={d}
                      data-testid={`dept-${d}`}
                      onClick={() => changeDept(d)}
                      variant={perms.department === d ? 'default' : 'outline'}
                      size="sm"
                      className={perms.department === d ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]' : 'border-[#262626] text-white hover:bg-[#262626]'}
                    >
                      {d.replace(/_/g, ' ')}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border-t border-[#262626] pt-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider">
                    Permissions ({perms.permissions.length}/{catalog.all_permissions.length})
                  </p>
                  <div className="flex items-center gap-3">
                    {/* iter163 — view-mode toggle (grouped is default; flat is the rollback fallback) */}
                    <div data-testid="perms-view-toggle" className="flex items-center bg-[#0A0A0A] border border-[#262626] rounded overflow-hidden">
                      <button
                        data-testid="perms-view-grouped"
                        onClick={() => setViewModePersisted('grouped')}
                        title="Grouped view"
                        className={`flex items-center gap-1 px-2 py-1 text-[11px] ${viewMode === 'grouped' ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'text-[#A3A3A3] hover:text-white'}`}
                      >
                        <LayoutGrid className="w-3 h-3" /> Grouped
                      </button>
                      <button
                        data-testid="perms-view-flat"
                        onClick={() => setViewModePersisted('flat')}
                        title="Flat (legacy) view"
                        className={`flex items-center gap-1 px-2 py-1 text-[11px] ${viewMode === 'flat' ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'text-[#A3A3A3] hover:text-white'}`}
                      >
                        <List className="w-3 h-3" /> Flat
                      </button>
                    </div>
                    <button onClick={resetToDefault} className="text-[#A3A3A3] hover:text-white text-xs">Reset to role defaults</button>
                  </div>
                </div>
                {viewMode === 'grouped' ? (
                  <GroupedPermissions
                    catalogPermissions={catalog.all_permissions}
                    selected={perms.permissions}
                    onChange={(next) => setPerms({ ...perms, permissions: next })}
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {catalog.all_permissions.map(p => (
                      <label key={p} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 cursor-pointer hover:border-[#D4AF37]">
                        <input
                          data-testid={`perm-${p}`}
                          type="checkbox"
                          checked={perms.permissions.includes(p)}
                          onChange={() => togglePerm(p)}
                          className="accent-[#D4AF37]"
                        />
                        <span className="text-white text-sm">{p.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <Button data-testid="save-perms" onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Save className="w-4 h-4 mr-2" /> Save Permissions</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPermissions;
